import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaArrowLeft, FaCheck, FaTimes } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { validateNavigationToken } from '../../../utils/urlSecurity';

const CourseModulesPage = () => {
  const { courseId: secureToken } = useParams();
  const navigate = useNavigate();
  const [courseId, setCourseId] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    status: 1,
    deleted: false,
    module_image: null,
    scorm_package: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  const validateToken = () => {
    const validation = validateNavigationToken(secureToken);
    
    if (!validation.valid) {
      if (validation.expired) {
        toast.error('Navigation token has expired. Please navigate from the courses page again.');
      } else {
        toast.error('Invalid navigation token. Please navigate from the courses page.');
      }
      navigate('/admin/courses');
      return false;
    }
    
    setCourseId(validation.courseId);
    setTokenValid(true);
    return true;
  };

  const fetchCourse = async () => {
    if (!courseId) return;
    
    try {
      const response = await api.courses.getById(courseId);
      if (response.data.success) {
        setCourse(response.data.data);
      } else {
        setCourse(response.data);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course details');
      navigate('/admin/courses');
    }
  };

  const fetchModules = async () => {
    if (!courseId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/modules/course/${courseId}`, {
        params: { includeDeleted: 'true' }
      });
      
      if (response.data.success) {
        setModules(response.data.data || []);
      } else {
        setModules(response.data || []);
      }
    } catch (error) {
      toast.error('Failed to load modules: ' + (error.response?.data?.message || error.message));
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (validateToken()) {
    }
  }, [secureToken]);
  
  useEffect(() => {
    if (tokenValid && courseId) {
      fetchCourse();
      fetchModules();
    }
  }, [courseId, tokenValid]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Module name is required';
    }
    
    if (formData.duration && (isNaN(parseInt(formData.duration, 10)) || !Number.isInteger(Number(formData.duration)) || parseInt(formData.duration, 10) < 1)) {
      errors.duration = 'Duration must be a positive integer (mins)';
    }

    if (!editingModule && !formData.module_image) {
      errors.module_image = 'Module image is required';
    }

    if (!editingModule && !formData.scorm_package) {
      errors.scorm_package = 'SCORM package is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowModal = (module = null) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        name: module.name || '',
        description: module.description || '',
        duration: module.duration?.toString() || '',
        status: module.status !== undefined && module.status !== null ? parseInt(module.status) : 1,
        deleted: module.deleted || false,
        module_image: module.module_image,
        scorm_package: null
      });
      setImagePreview(`${module.module_image}`);
    } else {
      setEditingModule(null);
      setFormData({
        name: '',
        description: '',
        duration: '',
        status: 1,
        deleted: false,
        module_image: null,
        scorm_package: null
      });
      setImagePreview(null);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModule(null);
    setFormErrors({});
    setModalLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
      if (name === 'module_image' && files[0]) {
        setImagePreview(URL.createObjectURL(files[0]));
      } else if (name === 'module_image') {
        setImagePreview(null);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (formErrors[name]) {
        setFormErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleStatusChange = (e) => {
    setFormData(prev => ({
      ...prev,
      status: parseInt(e.target.value)
    }));
  };

  const handleScormPackageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        scorm_package: file
      }));
      
      if (formErrors.scorm_package) {
        setFormErrors(prev => ({
          ...prev,
          scorm_package: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        scorm_package: null
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setModalLoading(true);
      
      const hasLargeFiles = formData.scorm_package || formData.module_image;
      if (hasLargeFiles) {
        toast.info('Uploading files... This may take a few minutes for large SCORM packages.', {
          position: "top-center",
          autoClose: 3000,
        });
      }
      
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('description', formData.description);
      payload.append('duration', formData.duration ? parseInt(formData.duration) : '');
      payload.append('sequence', editingModule ? editingModule.sequence : modules.length + 1);
      payload.append('status', parseInt(formData.status));
      
      if (formData.module_image) {
        payload.append('module_image', formData.module_image);
      }
      
      if (formData.scorm_package) {
        payload.append('scorm_package', formData.scorm_package);
      }

      const config = {
        timeout: 0,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload Progress: ${percentCompleted}%`);
          }
        }
      };

      if (editingModule) {
        const response = await api.put(`/modules/${editingModule.id}`, payload, config);
        
        let successMessage = `Module "${formData.name}" has been updated`;
        if (response.data.extractionMessage) {
          successMessage += `. ${response.data.extractionMessage}`;
        }
        toast.success(successMessage);
      } else {
        const response = await api.post(`/modules/course/${courseId}`, payload, config);
        
        let successMessage = `Module "${formData.name}" has been created`;
        if (response.data.extractionMessage) {
          successMessage += `. ${response.data.extractionMessage}`;
        }
        toast.success(successMessage);
      }
      
      handleCloseModal();
      fetchModules();
    } catch (error) {
      console.error('Error saving module:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Large SCORM packages may take longer. Please try again or check your connection.', {
          position: "top-center",
          autoClose: 8000,
        });
      } else {
        toast.error('Failed to save module: ' + (error.response?.data?.message || error.message));
      }
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (module) => {
    setModuleToDelete(module);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!moduleToDelete) return;
    
    try {
      await api.modules.delete(moduleToDelete.id);
      
      toast.success(`Module "${moduleToDelete.name}" has been deleted`);
      setShowDeleteModal(false);
      setModuleToDelete(null);
      fetchModules();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setModuleToDelete(null);
  };

  const filteredModules = modules.filter(module => {
    const searchFields = [
      module.name || '',
      module.description || ''
    ].join(' ').toLowerCase();
    
    const matchesSearch = !searchTerm || searchFields.includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    switch(filterStatus) {
      case 'active':
        matchesFilter = module.status === 1 && !module.deleted;
        break;
      case 'inactive':
        matchesFilter = module.status === 0 && !module.deleted;
        break;
      case 'deleted':
        matchesFilter = module.deleted;
        break;
      default:
        matchesFilter = !module.deleted;
        break;
    }
    
    return matchesSearch && matchesFilter;
  });
  
  const sortedModules = [...filteredModules].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  if (!tokenValid || loading && !course) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <Spinner animation="border" role="status" className="w-12 h-12 text-purple-600">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 lg:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
           <div className="mb-4">
              <Row className="align-items-center g-3">
                <Col md={2}>
                  <Button 
                    bsPrefix="custom-hamburger"
                    onClick={() => navigate('/admin/courses')}
                    className="w-full border border-purple-300 bg-white text-purple-600 hover:bg-purple-50 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm py-2 px-4"
                  >
                    <FaArrowLeft className="w-4 h-4" /> Back
                  </Button>
                </Col>
                <Col md={10}>
              <h5 className="m-0 text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              {course ? `Modules for: ${course.title || course.name}` : 'Course Modules'}
            </h5>
                </Col>
              </Row>
            </div>
      
     <Row className="mb-6 g-3">
    <Col xs={12} md={6} lg={5}>
      <InputGroup className="shadow-lg rounded-xl overflow-hidden">
        <InputGroup.Text className="bg-white border-0 text-purple-600 text-lg"><FaSearch /></InputGroup.Text>
        <Form.Control
          placeholder="Search modules by name or description..."
          value={searchTerm}
          onChange={handleSearch}
          className="border-0 focus:ring-4 focus:ring-purple-300 text-base placeholder-gray-500"
        />
      </InputGroup>
    </Col>
    <Col xs={12} sm={6} md={3} lg={3}>
      <Form.Select 
        value={filterStatus} 
        onChange={handleFilterChange} 
        className="h-full rounded-xl shadow-lg border-purple-200 focus:ring-4 focus:ring-purple-300 text-base"
      >
        <option value="all">All Modules</option>
        <option value="active">Active Modules</option>
        <option value="inactive">Inactive Modules</option>
        <option value="deleted">Deleted Modules</option>
      </Form.Select>
    </Col>
    <Col xs={12} sm={6} md={3} lg={4}>
      <Button 
            bsPrefix="custom-hamburger"
        onClick={() => handleShowModal()}
            className="w-full border border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm h-full p-2"
      >
        Add Module
      </Button>
    </Col>
  </Row>

      {sortedModules.length === 0 ? (
        <Alert variant="info" className="text-center py-10 rounded-2xl bg-white/80 backdrop-blur shadow-lg border border-purple-100">
          {searchTerm || filterStatus !== 'all' 
            ? 'No modules found matching your criteria.' 
            : 'No modules found. Click "Add Module" to create the first module.'
          }
        </Alert>
      ) : (
       <div className="overflow-x-auto rounded-2xl shadow-2xl bg-white">
    <Table bordered hover className="m-0 min-w-max sm:min-w-0">
      <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm sm:text-base">
        <tr>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold">#</th>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold">Module Name</th>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold hidden lg:table-cell">Description</th>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold">Duration (mins)</th>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold">Status</th>
          <th className="py-2 px-3 sm:py-4 sm:px-6 text-left font-bold">Actions</th>
        </tr>
      </thead>
      <tbody className="text-sm sm:text-base">
        {sortedModules.map((module, index) => (
          <tr key={module.id} className={`${module.deleted ? 'bg-red-50' : 'hover:bg-purple-50/70'} transition-all`}>
            <td className="py-2 px-3 sm:py-4 sm:px-6 font-medium">{index + 1}</td>
            <td className="py-2 px-3 sm:py-4 sm:px-6">
              <div className="text-gray-800">{module.name}</div>
              <div className="lg:hidden text-xs text-gray-600 mt-1">
                {module.description?.substring(0, 50)}{module.description?.length > 50 && '...'}
              </div>
            </td>
            <td className="py-2 px-3 sm:py-4 sm:px-6 hidden lg:table-cell">
              <small className="text-gray-800">{module.description ? (
                module.description.length > 100 
                  ? module.description.substring(0, 100) + '...' 
                  : module.description
              ) : '-'}</small>
            </td>
            <td className="py-2 px-3 sm:py-4 sm:px-6 text-left">{module.duration != null ? `${module.duration} mins` : '-'}</td>
            <td className="py-2 px-3 sm:py-4 sm:px-6 text-left">
              <Badge bg={module.status === 1 ? 'success' : 'danger'} className="px-3 py-1 text-xs sm:text-sm">
                {module.status === 1 ? 'Active' : 'Inactive'}
              </Badge>
              {module.deleted && (
                <Badge bg="warning" className="ms-1 px-2 py-1 text-xs">Deleted</Badge>
              )}
            </td>
            <td className="py-2 px-3 sm:py-4 sm:px-6">
              <div className="flex justify-left gap-2">
                <Button
                  bsPrefix="custom-hamburger"
                  size="sm"
                  onClick={() => handleShowModal(module)}
                  title="Edit Module"
                  disabled={module.deleted}
                  className="border-purple-400 text-purple-600 hover:bg-purple-100 rounded-lg"
                >
                  <FaEdit className="w-5 h-5" />
                </Button>
                {!module.deleted ? (
                  <Button
                  bsPrefix="custom-hamburger"
                    size="sm"
                    onClick={() => handleDeleteClick(module)}
                    title="Delete Module"
                    className="border-red-400 text-red-600 hover:bg-red-100 rounded-lg "
                  >
                    <FaTrash className="w-5 h-5" />
                  </Button>
                ) : (
                  <Button
                  bsPrefix="custom-hamburger"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.modules.restore(module.id);
                        toast.success(`Module "${module.name}" restored`);
                        fetchModules();
                      } catch (error) {
                        toast.error('Restore failed');
                      }
                    }}
                    title="Restore Module"
                    className="border-green-400 text-green-600 hover:bg-green-100 rounded-lg "
                  >
                    <FaCheck className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered size="md" className="rounded-2xl">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl">
            {editingModule ? `Edit Module: ${editingModule.name}` : 'Add New Module'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50">
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Module Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                isInvalid={!!formErrors.name}
                placeholder="Enter module name"
                className="rounded-xl border-purple-200 focus:ring-2 focus:ring-purple-500"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description" 
                value={formData.description}
                onChange={handleFormChange}
                isInvalid={!!formErrors.description}
                placeholder="Enter module description"
                required
                className="rounded-xl border-purple-200 focus:ring-2 focus:ring-purple-500"
              />
              <Form.Control.Feedback type="invalid">
                Description is required
              </Form.Control.Feedback>
            </Form.Group>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Duration (mins) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="duration"
                    type="number"
                    min="1"
                    step="1"
                    value={formData.duration}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.duration}
                    placeholder="Enter duration in mins"
                    required
                    className="rounded-xl border-purple-200 focus:ring-2 focus:ring-purple-500"
                  />
                  <Form.Control.Feedback type="invalid">
                    Duration is required and must be a positive integer (mins)
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status.toString()}
                    onChange={handleStatusChange}
                    className="rounded-xl border-purple-200 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">
                Module Image 
                {!editingModule && <span className="text-danger">*</span>}
                {editingModule && <span className="text-muted">(optional - leave empty to keep current image)</span>}
              </Form.Label>
              <Form.Control
                name="module_image"
                type="file"
                accept="image/*"
                onChange={handleFormChange}
                isInvalid={!!formErrors.module_image}
                required={!editingModule}
                className="rounded-xl"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.module_image}
              </Form.Control.Feedback>
              {imagePreview && (
                <div className="mt-3">
                  <div className="w-52 h-32 bg-gray-100 rounded-xl border border-gray-300 overflow-hidden shadow-md">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">
                SCORM Package 
                {!editingModule && <span className="text-danger">*</span>}
                {editingModule && <span className="text-muted">(optional - leave empty to keep current package)</span>}
              </Form.Label>
              <Form.Control
                name="scorm_package"
                type="file"
                accept=".zip"
                onChange={handleScormPackageChange}
                isInvalid={!!formErrors.scorm_package}
                required={!editingModule}
                className="rounded-xl"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.scorm_package}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Upload a SCORM package as a ZIP file only.
              </Form.Text>
              {formData.scorm_package && (
                <div className="mt-3 p-3 bg-purple-50 rounded-xl text-sm">
                  Selected: <strong>{formData.scorm_package.name}</strong>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger" 
                onClick={handleCloseModal} 
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleSave} 
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingModule ? 'Update Module' : 'Create Module'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Module</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{moduleToDelete ? moduleToDelete.name : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The module can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger" 
                onClick={handleDeleteCancel}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Delete Module
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseModulesPage;